import { forwardRef, memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css, CSSObject, SerializedStyles } from '@emotion/react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
  offset,
  Placement,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { isEqual } from 'lodash-es';
import { onlyText } from 'react-children-utilities';
import { useTranslation } from '@frontend/i18n';
import { ComponentProps } from '@frontend/types';
import { theme } from '@frontend/theme';
import {
  BaseFieldProps,
  Button,
  CaretDownIconSmall,
  CheckboxField,
  ChecklistField,
  ChecklistMenuField,
  Chip,
  FieldLayout,
  FieldLayoutWithAction,
  ListFieldProps,
  PopoverMenu,
  SearchField,
  SpinningLoader,
  useControlledField,
  useOnClickOutside,
  usePopoverMenu,
  useTooltip,
} from '@frontend/design-system';

const FLOATING_MENU_MAX_HEIGHT = 400;
const FLOATING_MENU_MIN_WIDTH = 220;
const SEARCH_THRESHOLD = 10;

/**
 * Calculates the total number of selectable options across all groups
 */
function calculateTotalValuesCount(groups: GroupOption[]): number {
  return groups.reduce((count, group) => {
    return count + (group.children.length === 0 ? 1 : group.children.length);
  }, 0);
}

/**
 * Filters groups and their children based on search term.
 * Matches both group labels and child option labels.
 */
function filterGroupsBySearchTerm(groups: GroupOption[], searchTerm: string): GroupOption[] {
  if (!searchTerm) return groups;

  const lowerSearchTerm = searchTerm.toLowerCase();

  return groups
    .map((group) => {
      const groupLabelText = typeof group.label === 'string' ? group.label : group.labelText ?? onlyText(group.label);
      const groupMatches = groupLabelText.toLowerCase().includes(lowerSearchTerm);

      if (group.children.length === 0) {
        return groupMatches ? group : null;
      }

      const filteredChildren = group.children.filter((child) => {
        const childLabelText = typeof child.label === 'string' ? child.label : child.labelText ?? onlyText(child.label);
        return groupMatches || childLabelText.toLowerCase().includes(lowerSearchTerm);
      });

      if (filteredChildren.length === 0) return null;

      return { ...group, children: filteredChildren };
    })
    .filter((group): group is GroupOption => group !== null);
}

/**
 * Calculates selection states for all groups
 */
function calculateGroupSelectionStates<T extends string>(
  groups: GroupOption[],
  selectedValues: T[]
): Map<string, GroupSelectionState> {
  const states = new Map<string, GroupSelectionState>();

  groups.forEach((group) => {
    const hasChildren = group.children.length > 0;

    // Paginated groups: the full roster is never loaded, so "all selected" is represented by
    // the group's own value being present, OR — for small rosters with all children loaded — by
    // every loaded child being selected once the loaded count reaches the server `totalCount`.
    if (group.paginatedGroup) {
      const enabledChildren = group.children.filter((child) => !child.disabled);
      const enabledChildValues = enabledChildren.map((child) => child.value as T);
      const selectedCount = enabledChildValues.filter((childValue) => selectedValues.includes(childValue)).length;
      const rosterTotal = group.totalCount ?? enabledChildValues.length;
      const allSelected =
        selectedValues.includes(group.value as T) || (rosterTotal > 0 && selectedCount === rosterTotal);

      states.set(group.value, {
        allSelected,
        hasChildren: true,
        isIndeterminate: !allSelected && selectedCount > 0,
        selectedCount,
        totalCount: rosterTotal,
      });
      return;
    }

    if (hasChildren) {
      const enabledChildren = group.children.filter((child) => !child.disabled);
      const enabledChildValues = enabledChildren.map((child) => child.value as T);
      const selectedCount = enabledChildValues.filter((childValue) => selectedValues.includes(childValue)).length;
      const totalCount = enabledChildValues.length;

      states.set(group.value, {
        allSelected: totalCount > 0 && selectedCount === totalCount,
        hasChildren: true,
        isIndeterminate: selectedCount > 0 && selectedCount < totalCount,
        selectedCount,
        totalCount,
      });
    } else {
      const isGroupSelected = selectedValues.includes(group.value as T);

      states.set(group.value, {
        allSelected: isGroupSelected,
        hasChildren: false,
        isIndeterminate: false,
        selectedCount: isGroupSelected ? 1 : 0,
        totalCount: 1,
      });
    }
  });

  return states;
}

/**
 * Gets all non-disabled selectable values from groups.
 */
function getAllSelectableValues<T extends string>(groups: GroupOption[]): T[] {
  const selectableValues: T[] = [];

  groups.forEach((group) => {
    if (group.children.length === 0) {
      selectableValues.push(group.value as T);
      return;
    }

    // For paginated groups, add the group's own value
    if (group.paginatedGroup) {
      selectableValues.push(group.value as T);
      return;
    }

    group.children.forEach((child) => {
      // Include locked children (they're selectable via group toggle) but exclude permanently disabled ones
      if (!child.disabled) {
        selectableValues.push(child.value as T);
      }
    });
  });

  const uniqueValues = new Set<string>();
  return selectableValues.filter((selectableValue) => {
    if (uniqueValues.has(selectableValue)) {
      return false;
    }
    uniqueValues.add(selectableValue);
    return true;
  });
}

/**
 * Creates a mapping of option values to their display labels.
 */
function createValueToLabelMap(groups: GroupOption[]): Record<string, string> {
  const map: Record<string, string> = {};

  groups.forEach((group) => {
    // Always map the group value itself (needed for displayValue group chips)
    map[group.value] = typeof group.label === 'string' ? group.label : group.labelText ?? onlyText(group.label);
    if (group.children.length === 0) {
      // leaf group already mapped above
    } else {
      group.children.forEach((child) => {
        map[child.value] = typeof child.label === 'string' ? child.label : child.labelText ?? onlyText(child.label);
      });
    }
  });

  return map;
}

/**
 * Custom hook that manages search functionality for grouped checklists.
 *
 * By default search is client-side: `groups` are filtered locally and the search bar only appears
 * once the option count exceeds `SEARCH_THRESHOLD`. When `serverSideSearch` is enabled the term is
 * controlled by the parent (via `searchTerm`/`onSearchChange`), local filtering is skipped, and the
 * search bar is always shown.
 */
function useGroupedChecklistSearch(
  groups: GroupOption[],
  serverSide = false,
  controlledSearchTerm?: string,
  onControlledSearchChange?: (term: string) => void
) {
  const [internalSearchTerm, setInternalSearchTerm] = useState('');

  const searchTerm = serverSide ? controlledSearchTerm ?? '' : internalSearchTerm;

  const setSearchTerm = useCallback(
    (newValue: string) => {
      if (serverSide) {
        onControlledSearchChange?.(newValue);
      } else {
        setInternalSearchTerm(newValue);
      }
    },
    [serverSide, onControlledSearchChange]
  );

  const totalValuesCount = useMemo(() => calculateTotalValuesCount(groups), [groups]);
  const showSearchBar = serverSide || totalValuesCount > SEARCH_THRESHOLD;
  const filteredGroups = useMemo(
    () => (serverSide ? groups : filterGroupsBySearchTerm(groups, searchTerm)),
    [groups, searchTerm, serverSide]
  );

  const searchFieldProps = useControlledField({
    onChange: (newValue: string) => setSearchTerm(newValue),
    type: 'text',
    value: searchTerm,
  });

  const clearSearch = useCallback(() => setSearchTerm(''), [setSearchTerm]);

  return {
    searchTerm,
    setSearchTerm,
    showSearchBar,
    filteredGroups,
    searchFieldProps,
    clearSearch,
  };
}

const LOAD_MORE_THRESHOLD_PX = 48;

/**
 * Fires `onLoadMore` when a scrollable element is scrolled within `LOAD_MORE_THRESHOLD_PX` of its
 * bottom, provided more pages are available and one is not already loading.
 */
function useInfiniteScroll({
  onLoadMore,
  hasMore,
  isLoadingMore,
}: {
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}) {
  return useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!onLoadMore || !hasMore || isLoadingMore) return;
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight <= LOAD_MORE_THRESHOLD_PX) {
        onLoadMore();
      }
    },
    [onLoadMore, hasMore, isLoadingMore]
  );
}

const LoadMoreSpinner = memo(() => (
  <div css={styles.loadMoreSpinner}>
    <SpinningLoader size='small' />
  </div>
));

LoadMoreSpinner.displayName = 'LoadMoreSpinner';

const SearchBarField = memo(
  ({
    searchFieldProps,
    cssStyle,
  }: {
    searchFieldProps: BaseFieldProps<string, '', HTMLInputElement>;
    cssStyle: SerializedStyles;
  }) => (
    <div css={cssStyle}>
      <SearchField
        {...searchFieldProps}
        name='search'
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Tab') {
            return;
          }
          e.stopPropagation();
        }}
      />
    </div>
  )
);

SearchBarField.displayName = 'SearchBarField';

type GroupedChildOption = {
  disabled?: boolean;
  /**
   * Visually disables the child (individual clicks do nothing) but still counts toward
   * the group's allSelected state and is included in group-level toggle operations.
   * Use this for dynamically locked items (e.g. selection-limit reached, group all-selected)
   * rather than permanently excluded items (use `disabled` for those).
   */
  locked?: boolean;
  tooltipText?: string;
  label: ReactNode;
  labelText?: string;
  value: string;
};

export type GroupOption = {
  children: GroupedChildOption[];
  hideLabel?: boolean;
  labelText?: string;
  label: ReactNode;
  value: string;
  trackingId?: string;
  /**
   * When true, selecting the group header adds a single value — the group's own `value`,
   * to the selection, rather than adding each child's value.
   * Unchecking the header removes it. Use this for paginated groups where the full child list is
   * never loaded, so "select all" cannot be represented by listing every individual child id.
   * The header's all-selected state is derived from that group-level value plus `totalCount`;
   * supply a chip label for it via the group's `label`/`labelText`.
   */
  paginatedGroup?: boolean;
  /**
   * Total number of selectable items in the group on the server (the full roster size), used only
   * by `paginatedGroup` groups. Lets the component detect "all selected" when every loaded child is
   * checked on a small roster
   */
  totalCount?: number;
};

type GroupedCheckListDropdownMenuProps<T extends string = string> = {
  disabled?: boolean;
  fieldContainerCss?: SerializedStyles;
  floatingStyleOverrides?: React.CSSProperties;
  groups: GroupOption[];
  label: string;
  name: string;
  onChange: (selected: T[]) => void;
  placement?: Placement;
  placeholder?: string;
  showLabel?: boolean;
  trackingIdBase?: string;
  value?: T[];
  showOverflowTooltip?: boolean;
  /** Constrains the scrollable content (parent groups) area to this height in px. */
  scrollableContentMaxHeight?: number;
  /** Fixed pixel width for the field box. Also used to compute per-chip max-width to prevent chip overflow. */
  width?: number;
  /**
   * Called whenever the staged (pre-Apply) selection changes.
   * Use this to get live selection updates without waiting for the Apply button.
   */
  onStagedSelectionChange?: (selection: T[]) => void;
  /**
   * When `false`, every checkbox change is committed to `onChange` immediately and the Apply button
   * is hidden. Use this when the parent already has its own Apply/submit step (e.g. a tray filter).
   * Defaults to `true`.
   */
  showApplyButton?: boolean;
  /**
   * Optional icon rendered as a start adornment inside the field.
   * Only shown when the menu is closed and no chips are selected.
   */
  renderIcon?: () => React.ReactNode;
  /**
   * Supplemental value -> label map merged on top of the labels derived from `groups`.
   * Needed because `groups` only contains the currently loaded page, so a selected item that was
   * scrolled or searched out of view would otherwise render its raw id in the chip. The parent
   * accumulates labels across pages and passes them here so chips always show a name.
   */
  labelOverrides?: Record<string, string>;
  /** When true, server-side search is used: client-side filtering is skipped and the search bar is always shown. */
  serverSideSearch?: boolean;
  /** Controlled search term (used with `serverSideSearch`). */
  searchTerm?: string;
  /** Called when the controlled search term changes (used with `serverSideSearch`). */
  onSearchChange?: (term: string) => void;
  /** Called when the scrollable content is near the bottom, to request the next page. */
  onLoadMore?: () => void;
  /** Whether more pages are available to load. */
  hasMore?: boolean;
  /** Whether the next page is currently loading (renders a spinner at the bottom of the list). */
  isLoadingMore?: boolean;
};

type GroupSelectionState = {
  allSelected: boolean;
  hasChildren: boolean;
  isIndeterminate: boolean;
  selectedCount: number;
  totalCount: number;
};

type GroupCheckboxProps = {
  disabled?: boolean;
  indeterminate: boolean;
  isChecked: boolean;
  label: ReactNode;
  name: string;
  onChange: (value: boolean) => void;
  trackingId?: string;
};

type ChipInputFieldWrapperProps = {
  chipMaxWidth?: number;
  keyToLabelMap: Record<string, string>;
  onRemoveClick: (chipValue: string) => void;
  selectedKeys: string[];
  showOverflowTooltip?: boolean;
};

type ChipDropdownMenuCustomStyles = {
  applyButton?: CSSObject;
  checkListWrapper?: CSSObject;
  chipDropdownFilter?: CSSObject;
  popoverMenuWrapper?: CSSObject;
  topBar?: CSSObject;
};

type GroupedChecklistChipDropdownMenuProps<T extends string = string> = {
  customStyles?: ChipDropdownMenuCustomStyles;
  defaultValue?: T[];
  disabled?: boolean;
  groups: GroupOption[];
  label: string;
  onChange: (selected: T[]) => void;
  /**
   * Called whenever the staged (pre-Apply) selection changes.
   * Use this to get live selection updates without waiting for the Apply button.
   */
  onStagedSelectionChange?: (selection: T[]) => void;
  renderIcon?: (isFilterActive: boolean, isOpen?: boolean) => React.ReactNode;
  trackingId?: string;
  value?: T[];
  /** Constrains the scrollable content (parent groups) area to this height in px. */
  scrollableContentMaxHeight?: number;
  /**
   * Supplemental value -> label map merged on top of the labels derived from `groups`.
   * See {@link GroupedCheckListDropdownMenuProps.labelOverrides}.
   */
  labelOverrides?: Record<string, string>;
  /** When true, server-side search is used: client-side filtering is skipped and the search bar is always shown. */
  serverSideSearch?: boolean;
  /** Controlled search term (used with `serverSideSearch`). */
  searchTerm?: string;
  /** Called when the controlled search term changes (used with `serverSideSearch`). */
  onSearchChange?: (term: string) => void;
  /** Called when the scrollable content is near the bottom, to request the next page. */
  onLoadMore?: () => void;
  /** Whether more pages are available to load. */
  hasMore?: boolean;
  /** Whether the next page is currently loading (renders a spinner at the bottom of the list). */
  isLoadingMore?: boolean;
};

const GroupCheckbox = memo(
  ({ disabled, indeterminate, isChecked, label, name, onChange, trackingId }: GroupCheckboxProps) => {
    const checkboxFieldProps = useControlledField({
      onChange: (newValue: boolean) => {
        onChange(newValue);
      },
      type: 'checkbox',
      value: isChecked,
    });

    return (
      <CheckboxField
        {...checkboxFieldProps}
        css={styles.groupCheckbox}
        disabled={disabled}
        indeterminate={indeterminate}
        label={label}
        labelPlacement='right'
        name={name}
        trackingId={trackingId}
      />
    );
  }
);

GroupCheckbox.displayName = 'GroupCheckbox';

const LockedChildOption = memo(
  ({
    child,
    hideGroupLabel,
    tooltipText,
    trackingIdBase,
    forceChecked = false,
  }: {
    child: GroupedChildOption;
    hideGroupLabel: boolean;
    tooltipText?: string;
    trackingIdBase?: string;
    /**
     * Render the option as checked (and disabled) regardless of the checklist value. Used for
     * paginated groups whose "select all" is represented by the group's own value in the selection, so the individual
     * child ids are not in the selection but should still appear selected to the user.
     */
    forceChecked?: boolean;
  }) => {
    const { Tooltip, triggerProps, tooltipProps } = useTooltip({ placement: 'top' });
    const forceCheckedFieldProps = useControlledField({ onChange: () => undefined, type: 'checkbox', value: true });

    return (
      <div css={styles.childOption(hideGroupLabel)} {...(tooltipText ? triggerProps : {})}>
        {forceChecked ? (
          <CheckboxField
            {...forceCheckedFieldProps}
            css={styles.childCheckbox}
            disabled
            label={child.label}
            labelPlacement='right'
            name={child.value}
            trackingId={`${trackingIdBase}-option-${child.value.toLowerCase()}`}
          />
        ) : (
          <ChecklistField.Option
            css={styles.childCheckbox}
            disabled
            name={child.value}
            trackingId={`${trackingIdBase}-option-${child.value.toLowerCase()}`}
          >
            {child.label}
          </ChecklistField.Option>
        )}
        {tooltipText && <Tooltip {...tooltipProps}>{tooltipText}</Tooltip>}
      </div>
    );
  }
);

LockedChildOption.displayName = 'LockedChildOption';

const ChipInputFieldWrapper = memo(
  forwardRef<HTMLDivElement, ChipInputFieldWrapperProps>(
    ({ chipMaxWidth, selectedKeys, keyToLabelMap, onRemoveClick, showOverflowTooltip, ...props }, ref) => {
      return (
        <div ref={ref} {...props} style={styles.chipInputContainer}>
          <div
            css={[
              styles.chipInputWrapper,
              chipMaxWidth !== undefined &&
                css`
                  .chip {
                    max-width: ${chipMaxWidth}px;
                  }
                `,
            ]}
          >
            <ChecklistMenuField.ChipInput
              keyToLabelMap={keyToLabelMap}
              onRemoveClick={onRemoveClick}
              selectedKeys={selectedKeys}
              showOverflowTooltip={showOverflowTooltip}
              variant='primary'
            />
          </div>
        </div>
      );
    }
  )
);

ChipInputFieldWrapper.displayName = 'ChipInputFieldWrapper';

const GroupedChecklistContent = memo(
  ({
    checklistProps,
    disabled,
    groups,
    groupStates,
    label,
    name,
    onToggleGroup,
    showLabel,
    trackingIdBase,
  }: {
    checklistProps: ListFieldProps;
    disabled?: boolean;
    groups: GroupOption[];
    groupStates: Map<string, GroupSelectionState>;
    label: string;
    name: string;
    onToggleGroup: (group: GroupOption) => void;
    showLabel?: boolean;
    trackingIdBase?: string;
  }) => {
    return (
      <ChecklistField
        {...checklistProps}
        css={styles.checklist(showLabel)}
        disabled={disabled ? true : undefined}
        label={label}
        name={name}
      >
        {groups.map((group) => {
          const state = groupStates.get(group.value);
          const hasChildren = group.children.length > 0;
          const hideGroupLabel = group.hideLabel ?? false;

          return (
            <div css={styles.group} key={group.value}>
              {!hideGroupLabel && (
                <div css={styles.groupHeader(hasChildren)}>
                  <GroupCheckbox
                    disabled={disabled}
                    indeterminate={state?.isIndeterminate ?? false}
                    isChecked={state?.isIndeterminate ? true : state?.allSelected ?? false}
                    label={group.label}
                    name={group.value}
                    onChange={() => onToggleGroup(group)}
                    trackingId={`${trackingIdBase}-option-${group.trackingId ?? group.value.toLowerCase()}`}
                  />
                </div>
              )}

              {hasChildren && (
                <>
                  {group.children.map((child) => {
                    if (child.locked) {
                      return (
                        <LockedChildOption
                          key={child.value}
                          child={child}
                          hideGroupLabel={hideGroupLabel}
                          tooltipText={child.tooltipText}
                          trackingIdBase={trackingIdBase}
                          // In a paginated group, "select all" is stored as the group's own value (not
                          // individual ids), so force the locked children to appear checked when the
                          // group is all-selected.
                          forceChecked={!!(group.paginatedGroup && state?.allSelected)}
                        />
                      );
                    }

                    return (
                      <div css={styles.childOption(hideGroupLabel)} key={child.value}>
                        <ChecklistField.Option
                          css={styles.childCheckbox}
                          disabled={child.disabled || !!child.locked}
                          name={child.value}
                          trackingId={`${trackingIdBase}-option-${child.value.toLowerCase()}`}
                        >
                          {child.label}
                        </ChecklistField.Option>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          );
        })}
      </ChecklistField>
    );
  }
);

GroupedChecklistContent.displayName = 'GroupedChecklistContent';

export function GroupedCheckListDropdownMenu<T extends string = string>({
  disabled,
  fieldContainerCss,
  floatingStyleOverrides,
  groups,
  label,
  name,
  onChange,
  onStagedSelectionChange,
  placement = 'bottom',
  placeholder = 'Select one',
  renderIcon,
  showApplyButton = true,
  showLabel = false,
  showOverflowTooltip = false,
  scrollableContentMaxHeight,
  trackingIdBase,
  value = [],
  width,
  labelOverrides,
  serverSideSearch = false,
  searchTerm,
  onSearchChange,
  onLoadMore,
  hasMore,
  isLoadingMore,
}: GroupedCheckListDropdownMenuProps<T>) {
  const { t } = useTranslation('call-intelligence');

  // Staged (pre-Apply) selection — only committed to `onChange` when Apply is clicked
  const [stagedSelection, setStagedSelection] = useState<T[]>(value);

  // Sync internal state when external value changes (e.g. chip removal from the field)
  useEffect(() => {
    setStagedSelection(value);
  }, [value]);

  // Notify parent of live (pre-Apply) selection changes so it can react in real-time
  // (e.g. updating locked/disabled states on other checkboxes)
  useEffect(() => {
    onStagedSelectionChange?.(stagedSelection);
  }, [stagedSelection, onStagedSelectionChange]);

  // padding (16px) + caret icon + gap (24px) = 40px always reserved
  // "+N more" chip (~60px) + chip gap (8px) = 68px only reserved when multiple items are selected
  const chipMaxWidth = width !== undefined ? width - 40 - (stagedSelection.length > 1 ? 68 : 0) : undefined;

  // Commits newValue to stagedSelection. When showApplyButton=false, also calls onChange immediately.
  const updateStagedSelection = useCallback(
    (newValue: T[]) => {
      setStagedSelection(newValue);
      if (!showApplyButton) {
        onChange(newValue);
      }
    },
    [onChange, showApplyButton]
  );
  const [menuState, setMenuState] = useState({
    isOpen: false,
  });

  const startIcon = renderIcon && !menuState.isOpen && stagedSelection.length === 0 ? renderIcon() : undefined;

  const { showSearchBar, filteredGroups, searchFieldProps, clearSearch } = useGroupedChecklistSearch(
    groups,
    serverSideSearch,
    searchTerm,
    onSearchChange
  );

  const handleScroll = useInfiniteScroll({ onLoadMore, hasMore, isLoadingMore });

  const checklistFieldProps = useControlledField({
    onChange: (newValue) => {
      updateStagedSelection(newValue as T[]);
    },
    type: 'checklist',
    value: stagedSelection as string[],
  });

  const floatingNodeId = useFloatingNodeId();

  const floatingConfig = useMemo(
    () => ({
      middleware: [
        offset(placement === 'top' ? 12 : 3),
        flip(),
        size({
          apply({ elements, rects }) {
            Object.assign(elements.floating.style, {
              width: `${rects.reference.width}px`,
            });
          },
        }),
      ],
      nodeId: floatingNodeId,
      onOpenChange: (nextOpen: boolean) => {
        setMenuState({ isOpen: nextOpen });
        if (!nextOpen) {
          clearSearch();
          if (showApplyButton) {
            // Discard staged changes when menu closes without Apply
            setStagedSelection(value);
          }
        }
      },
      open: menuState.isOpen,
      placement,
      whileElementsMounted: autoUpdate,
    }),
    [floatingNodeId, menuState.isOpen, placement, clearSearch]
  );

  const { refs, context, floatingStyles } = useFloating<HTMLInputElement>(floatingConfig);

  const { getReferenceProps } = useInteractions([
    useClick(context),
    useRole(context, { role: 'listbox' }),
    useDismiss(context),
  ]);

  const groupSelectionStates = useMemo(
    () => calculateGroupSelectionStates(groups, stagedSelection),
    [groups, stagedSelection]
  );

  const leafGroups = useMemo(() => filteredGroups.filter((g) => g.children.length === 0), [filteredGroups]);
  const parentGroups = useMemo(() => filteredGroups.filter((g) => g.children.length > 0), [filteredGroups]);

  const handleToggleGroupSelection = useCallback(
    (group: GroupOption) => {
      const state = groupSelectionStates.get(group.value);

      // Paginated groups add their own value instead of enumerating children, since the full child
      // list is never loaded. The parent interprets that value on change.
      if (group.paginatedGroup) {
        const groupValue = group.value as T;
        const childValues = group.children.map((child) => child.value) as T[];
        const isSelected = state?.allSelected ?? stagedSelection.includes(groupValue);
        if (isSelected) {
          // Clear the group value and any individually selected children (covers count-equality all-selected).
          updateStagedSelection(stagedSelection.filter((v) => v !== groupValue && !childValues.includes(v)));
        } else {
          // Add the group value; drop individual child selections since it supersedes them.
          updateStagedSelection([...stagedSelection.filter((v) => !childValues.includes(v)), groupValue]);
        }
        return;
      }

      if (!state?.hasChildren) {
        const parentValue = group.value as T;
        const isSelected = stagedSelection.includes(parentValue);

        updateStagedSelection(
          isSelected ? stagedSelection.filter((v) => v !== parentValue) : [...stagedSelection, parentValue]
        );
      } else {
        const enabledChildValues = group.children.filter((child) => !child.disabled).map((child) => child.value) as T[];

        if (state?.allSelected) {
          updateStagedSelection(stagedSelection.filter((v) => !enabledChildValues.includes(v)));
        } else {
          const updatedValue = [...stagedSelection];
          enabledChildValues.forEach((childValue) => {
            if (!updatedValue.includes(childValue)) {
              updatedValue.push(childValue);
            }
          });
          updateStagedSelection(updatedValue);
        }
      }
    },
    [groupSelectionStates, stagedSelection, updateStagedSelection]
  );

  const handleApply = useCallback(() => {
    onChange(stagedSelection);
    setMenuState({ isOpen: false });
    clearSearch();
  }, [onChange, stagedSelection, clearSearch]);

  const valueToLabelMap = useMemo(
    () => ({ ...createValueToLabelMap(groups), ...labelOverrides }),
    [groups, labelOverrides]
  );

  // Auto-collapse chips: for each group whose all children are in `stagedSelection`,
  // replace the individual child chips with a single group-level chip.
  // Uses stagedSelection so chips update live as the user makes selections.
  const displayValue = useMemo(() => {
    const collapsedChips: string[] = [];
    let collapsed = false;

    groups.forEach((group) => {
      // Paginated groups collapse to a single group-value chip when the group is all-selected
      // otherwise their loaded children render as individual chips.
      if (group.paginatedGroup) {
        if (groupSelectionStates.get(group.value)?.allSelected) {
          collapsedChips.push(group.value);
          collapsed = true;
        } else {
          group.children.forEach((c) => {
            if (stagedSelection.includes(c.value as T)) {
              collapsedChips.push(c.value);
            }
          });
        }
        return;
      }

      if (group.children.length === 0) {
        // Leaf group — keep as-is
        if (stagedSelection.includes(group.value as T)) {
          collapsedChips.push(group.value);
        }
        return;
      }

      const enabledChildren = group.children.filter((c) => !c.disabled);
      const allSelected =
        enabledChildren.length > 0 && enabledChildren.every((c) => stagedSelection.includes(c.value as T));

      if (allSelected) {
        collapsedChips.push(group.value);
        collapsed = true;
      } else {
        group.children.forEach((c) => {
          if (stagedSelection.includes(c.value as T)) {
            collapsedChips.push(c.value);
          }
        });
      }
    });

    // Only return a custom displayValue when at least one group was collapsed;
    // otherwise let selectedKeys fall back to `stagedSelection` unchanged.
    return collapsed ? (collapsedChips as T[]) : undefined;
  }, [groups, stagedSelection, groupSelectionStates]);

  const handleRemoveChip = useCallback(
    ({ chipValue }: { chipValue: string }) => {
      const removeFrom = (selection: T[]): T[] => {
        if (!selection.includes(chipValue as T)) {
          // Chip represents a collapsed group — remove all its children
          const group = groups.find((g) => g.value === chipValue);
          if (group) {
            const childValues = new Set(group.children.map((c) => c.value));
            return selection.filter((v) => !childValues.has(v)) as T[];
          }
          return selection;
        }
        return selection.filter((v) => v !== chipValue) as T[];
      };

      if (menuState.isOpen) {
        // Menu is open: update staged selection (updateStagedSelection will also call onChange if no Apply button)
        updateStagedSelection(removeFrom(stagedSelection));
      } else {
        // Menu is closed: always commit immediately
        onChange(removeFrom(value));
      }
    },
    [onChange, value, stagedSelection, groups, menuState.isOpen, updateStagedSelection]
  );

  const chipInputFieldProps = useMemo(
    () => ({
      chipMaxWidth,
      keyToLabelMap: valueToLabelMap,
      onRemoveClick: (chipValue: string) => handleRemoveChip({ chipValue }),
      selectedKeys: (displayValue ?? stagedSelection) as string[],
      showOverflowTooltip,
    }),
    [chipMaxWidth, displayValue, handleRemoveChip, stagedSelection, valueToLabelMap, showOverflowTooltip]
  );

  const handleToggleMenu = useCallback(() => {
    setMenuState((prevState) => ({ isOpen: !prevState.isOpen }));
  }, []);

  const floatingMenuStyles = useMemo(
    () => ({
      ...floatingStyles,
      backgroundColor: theme.colors.white,
      borderRadius: theme.borderRadius.medium,
      boxShadow: theme.shadows.heavy,
      display: 'flex' as const,
      flexDirection: 'column' as const,
      maxHeight: FLOATING_MENU_MAX_HEIGHT,
      minWidth: FLOATING_MENU_MIN_WIDTH,
      paddingBottom: theme.spacing(1),
      zIndex: theme.zIndex.popover,
      ...floatingStyleOverrides,
    }),
    [floatingStyles, floatingStyleOverrides]
  );

  return (
    <FieldLayout
      outerProps={{ 'data-trackingId': trackingIdBase }}
      {...checklistFieldProps}
      {...getReferenceProps()}
      containerCss={[
        styles.fieldContainer,
        width !== undefined &&
          css`
            width: ${width}px;
          `,
        fieldContainerCss,
      ]}
      endAdornment={
        <CaretDownIconSmall
          color='subdued'
          css={[
            css`
              cursor: pointer;
              transition: transform 250ms ease-out;
              pointer-events: ${menuState.isOpen ? 'none' : 'auto'};
            `,

            menuState.isOpen &&
              css`
                transform: rotate(180deg);
              `,
          ]}
          name='alt-caret-down-tiny'
          onClick={handleToggleMenu}
          size={16}
        />
      }
      field={ChipInputFieldWrapper as unknown as ComponentProps<typeof FieldLayoutWithAction>['field']}
      fieldComponentProps={chipInputFieldProps}
      hasPadding={false}
      label={label}
      name={name}
      onChange={() => {}}
      placeholder={placeholder}
      ref={refs.setReference}
      startAdornment={startIcon}
      startAdornmentWidth={startIcon ? 8 : undefined}
      value={value}
      active={menuState.isOpen}
    >
      <FloatingNode id={floatingNodeId}>
        {menuState.isOpen && (
          <FloatingPortal>
            <FloatingFocusManager context={context} initialFocus={0}>
              <div style={floatingMenuStyles} ref={refs.setFloating}>
                {showSearchBar && <SearchBarField searchFieldProps={searchFieldProps} cssStyle={styles.searchBar} />}
                {leafGroups.length > 0 && (
                  <div css={styles.pinnedContent}>
                    <GroupedChecklistContent
                      checklistProps={checklistFieldProps}
                      disabled={disabled}
                      groups={leafGroups}
                      groupStates={groupSelectionStates}
                      label={label}
                      name={`${name}-pinned`}
                      onToggleGroup={handleToggleGroupSelection}
                      showLabel={showLabel}
                      trackingIdBase={trackingIdBase}
                    />
                  </div>
                )}
                {parentGroups.length > 0 && (
                  <div
                    onScroll={handleScroll}
                    css={[
                      styles.floatingScrollableContent,
                      scrollableContentMaxHeight !== undefined &&
                        css`
                          max-height: ${scrollableContentMaxHeight}px;
                        `,
                    ]}
                  >
                    <GroupedChecklistContent
                      checklistProps={checklistFieldProps}
                      disabled={disabled}
                      groups={parentGroups}
                      groupStates={groupSelectionStates}
                      label={label}
                      name={name}
                      onToggleGroup={handleToggleGroupSelection}
                      showLabel={showLabel}
                      trackingIdBase={trackingIdBase}
                    />
                    {isLoadingMore && <LoadMoreSpinner />}
                  </div>
                )}

                {showApplyButton && (
                  <Button
                    css={styles.applyButton}
                    disabled={disabled}
                    onClick={handleApply}
                    trackingId={trackingIdBase ? `${trackingIdBase}-apply-button` : undefined}
                  >
                    {t('Apply')}
                  </Button>
                )}
              </div>
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </FloatingNode>
    </FieldLayout>
  );
}

export function GroupedChecklistChipDropdownMenu<T extends string = string>({
  customStyles,
  defaultValue = [],
  disabled,
  groups,
  label,
  onChange,
  onStagedSelectionChange,
  renderIcon,
  scrollableContentMaxHeight,
  trackingId,
  value,
  serverSideSearch = false,
  searchTerm,
  onSearchChange,
  onLoadMore,
  hasMore,
  isLoadingMore,
}: GroupedChecklistChipDropdownMenuProps<T>) {
  const { t } = useTranslation('call-intelligence');
  const { close, getMenuProps, getTriggerProps, isOpen, open } = usePopoverMenu({
    placement: 'bottom-start',
  });
  const { ref: filterTriggerRef, ...triggerProps } = getTriggerProps();
  const [stagedSelection, setStagedSelection] = useState<T[]>(value || []);

  const { showSearchBar, filteredGroups, searchFieldProps, clearSearch } = useGroupedChecklistSearch(
    groups,
    serverSideSearch,
    searchTerm,
    onSearchChange
  );

  const handleScroll = useInfiniteScroll({ onLoadMore, hasMore, isLoadingMore });

  const outsideClickRef = useRef<HTMLDivElement>(null);
  const isFilterActive = !isEqual([...defaultValue].sort(), [...(value || [])].sort());

  const leftElement = useMemo(() => {
    return renderIcon ? renderIcon(isFilterActive, isOpen) : null;
  }, [renderIcon, isFilterActive, isOpen]);

  useEffect(() => {
    setStagedSelection(value || []);
  }, [value]);

  useEffect(() => {
    onStagedSelectionChange?.(stagedSelection);
  }, [stagedSelection, onStagedSelectionChange]);

  useEffect(() => {
    if (!isOpen) {
      clearSearch();
    }
  }, [isOpen, clearSearch]);

  const handleCancelSelection = useCallback(() => {
    setStagedSelection(value || []);
    close();
  }, [close, value]);

  useOnClickOutside({
    handler: handleCancelSelection,
    ref: outsideClickRef,
  });

  const handleApplySelection = useCallback(() => {
    onChange(stagedSelection);
    close();
  }, [close, stagedSelection, onChange]);

  const groupSelectionStates = useMemo(
    () => calculateGroupSelectionStates(groups, stagedSelection),
    [groups, stagedSelection]
  );

  const leafGroups = useMemo(() => filteredGroups.filter((g) => g.children.length === 0), [filteredGroups]);
  const parentGroups = useMemo(() => filteredGroups.filter((g) => g.children.length > 0), [filteredGroups]);

  const handleToggleGroupSelection = useCallback(
    (group: GroupOption) => {
      const state = groupSelectionStates.get(group.value);

      // Paginated groups add their own value instead of enumerating children.
      if (group.paginatedGroup) {
        const groupValue = group.value as T;
        const childValues = group.children.map((child) => child.value) as T[];
        const isSelected = state?.allSelected ?? false;
        setStagedSelection((previousSelection) =>
          isSelected
            ? previousSelection.filter(
                (selectedValue) => selectedValue !== groupValue && !childValues.includes(selectedValue)
              )
            : [...previousSelection.filter((selectedValue) => !childValues.includes(selectedValue)), groupValue]
        );
        return;
      }

      if (!state?.hasChildren) {
        const groupValue = group.value as T;
        setStagedSelection((previousSelection) =>
          previousSelection.includes(groupValue)
            ? previousSelection.filter((selectedValue) => selectedValue !== groupValue)
            : [...previousSelection, groupValue]
        );
        return;
      }

      // Include locked children (not permanently disabled) so group toggle bypasses individual locks
      const toggleableChildren = group.children.filter((child) => !child.disabled).map((child) => child.value as T);

      setStagedSelection((previousSelection) => {
        if (state.allSelected) {
          return previousSelection.filter((selectedValue) => !toggleableChildren.includes(selectedValue));
        }

        const updatedSelection = [...previousSelection];
        toggleableChildren.forEach((childValue) => {
          if (!updatedSelection.includes(childValue)) {
            updatedSelection.push(childValue);
          }
        });
        return updatedSelection;
      });
    },
    [groupSelectionStates]
  );

  const allSelectableValues = useMemo(() => getAllSelectableValues<T>(groups), [groups]);

  const handleSelectAll = useCallback(() => {
    setStagedSelection(allSelectableValues);
  }, [allSelectableValues]);

  const handleClearAll = useCallback(() => {
    setStagedSelection([]);
  }, []);

  const checklistFieldProps = useControlledField({
    onChange: (newValue) => setStagedSelection(newValue as T[]),
    type: 'checklist',
    value: stagedSelection as string[],
  });

  return (
    <div ref={filterTriggerRef}>
      <Chip.DropdownFilter
        {...triggerProps}
        css={{
          gap: theme.spacing(1),
          maxWidth: 'none',
          ...customStyles?.chipDropdownFilter,
        }}
        filterIsActive={isFilterActive}
        leftElement={leftElement}
        onClick={open}
        trackingId={trackingId}
      >
        {label}
      </Chip.DropdownFilter>

      <PopoverMenu {...getMenuProps()}>
        <div css={[styles.checkListWrapper, customStyles?.popoverMenuWrapper]} ref={outsideClickRef}>
          <div css={[styles.topBar, customStyles?.topBar]}>
            <Button onClick={handleSelectAll} variant='tertiary' disabled={disabled}>
              {t('Select all')}
            </Button>
            <Button onClick={handleClearAll} variant='tertiary' disabled={disabled}>
              {t('Clear')}
            </Button>
          </div>

          {showSearchBar && <SearchBarField searchFieldProps={searchFieldProps} cssStyle={styles.searchBar} />}

          {leafGroups.length > 0 && (
            <div css={styles.pinnedContent}>
              <GroupedChecklistContent
                checklistProps={checklistFieldProps}
                disabled={disabled}
                groups={leafGroups}
                groupStates={groupSelectionStates}
                label={label}
                name={`${label}-pinned`}
                onToggleGroup={handleToggleGroupSelection}
                showLabel={false}
                trackingIdBase={trackingId}
              />
            </div>
          )}
          {parentGroups.length > 0 && (
            <div
              onScroll={handleScroll}
              css={[
                styles.scrollableContent,
                customStyles?.checkListWrapper,
                scrollableContentMaxHeight !== undefined &&
                  css`
                    max-height: ${scrollableContentMaxHeight}px;
                    overflow-y: scroll;
                    overflow-x: hidden;
                  `,
              ]}
            >
              <GroupedChecklistContent
                checklistProps={checklistFieldProps}
                disabled={disabled}
                groups={parentGroups}
                groupStates={groupSelectionStates}
                label={label}
                name={label}
                onToggleGroup={handleToggleGroupSelection}
                showLabel={false}
                trackingIdBase={trackingId}
              />
              {isLoadingMore && <LoadMoreSpinner />}
            </div>
          )}

          <Button
            css={[styles.applyButton, customStyles?.applyButton]}
            disabled={disabled}
            onClick={handleApplySelection}
            trackingId={trackingId ? `${trackingId}-apply-button` : undefined}
          >
            {t('Apply')}
          </Button>
        </div>
      </PopoverMenu>
    </div>
  );
}

const styles = {
  checklist: (showLabel?: boolean) => css`
    > label {
      display: ${showLabel ? 'block' : 'none'};
    }

    > div {
      align-items: center;
    }

    > div:last-of-type {
      margin-bottom: 0;
    }
  `,

  childCheckbox: css`
    margin: 0;
  `,

  childOption: (hideGroupLabel?: boolean) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    padding: ${hideGroupLabel ? theme.spacing(1, 0) : theme.spacing(1, 1, 1, 2)};

    > div {
      width: 100%;
      overflow-wrap: break-word;
      word-break: break-word;
      min-width: 0;

      > label {
        width: 100%;
      }
    }
  `,

  group: css`
    border-bottom: 1px solid ${theme.colors.neutral20};
    padding: ${theme.spacing(1, 2)};
    &:last-of-type {
      border-bottom: none;
    }
  `,

  groupCheckbox: css`
    margin: 0;
    align-items: center;
    width: 100%;
    > label {
      width: 100%;
    }
  `,

  groupCheckboxOption: css`
    margin: 0;
    > label {
      display: none;
    }
  `,

  groupHeader: (hasChildren: boolean) => css`
    position: sticky;
    top: 0;
    z-index: ${theme.zIndex.middle};
    background-color: ${theme.colors.white};
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    padding-bottom: ${theme.spacing(hasChildren ? 1 : 0)};
    /* Extend background to cover the parent group's top and side padding so there's no gap behind the sticky header */
    padding-top: ${theme.spacing(1)};
    margin-top: -${theme.spacing(1)};
    margin-left: -${theme.spacing(2)};
    margin-right: -${theme.spacing(2)};
    padding-left: ${theme.spacing(2)};
    padding-right: ${theme.spacing(2)};
  `,

  chipInputContainer: {
    height: '100%',
    width: '100%',
  },

  chipInputWrapper: css`
    display: flex;
    flex-wrap: wrap;
    width: 100%;

    .chip {
      background-color: ${theme.colors.primary5};
      color: ${theme.font.colors.default};

      svg {
        color: ${theme.colors.primary90};
      }
    }
  `,

  fieldContainer: css`
    > div {
      height: 40px;
      padding: ${theme.spacing(1, 1)};
    }

    cursor: pointer;
  `,
  /******* */

  pinnedContent: css`
    flex-shrink: 0;
    border-bottom: 1px solid ${theme.colors.neutral20};
  `,

  floatingScrollableContent: css`
    overflow-y: scroll;
    overflow-x: hidden;
    flex: 1;
  `,
  loadMoreSpinner: css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${theme.spacing(1)};
  `,
  searchBar: css`
    padding: ${theme.spacing(1, 2)};
  `,
  topBar: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${theme.spacing(0.25, 2)};

    button {
      color: ${theme.colors.text.interactive};
    }
  `,
  checkListWrapper: css`
    display: flex;
    flex-direction: column;
    width: 220px;
    max-height: calc(100vh - 100px);
    overflow: hidden;
  `,
  scrollableContent: css`
    overflow: auto;
    flex: 1;
    min-height: 0;
  `,
  applyButton: css`
    margin: auto;
    width: calc(100% - ${theme.spacing(2)});
    bottom: 0;
    position: sticky;
  `,
};
